// Facturatie in schijven en correcties (blok C deel 2):
//  - percentage dat nu gefactureerd mag worden, met reden; de rest pas na oplevering (SPEC_workflow 6.3, calculatie 11);
//  - correctie nadat de onderaannemer al betaald is, en de verrekening op de onderaannemer (6.5).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORIE_LABELS, fmtPercentage, type FactuurPercentage } from '@recotex/shared';
import type { OaVerrekening } from '../lib/api';
import { fmtDate, fmtDateTime, fmtEUR } from '../lib/format';

export function FactuurPercentageBlok({ percentage, totaal, nuFactureerbaar, percentageRest, openPosten, formOpen, magWijzigen, magVrijgeven, bezig, onOpslaan, onSluitForm, onVrijgeven }: {
  percentage: FactuurPercentage | null;
  totaal: number;
  nuFactureerbaar: number;
  percentageRest: number;
  /** Deel dat per post open staat: het percentage geldt op de rest. */
  openPosten: number;
  formOpen: boolean;
  magWijzigen: boolean;
  magVrijgeven: boolean;
  bezig: boolean;
  onOpslaan: (p: { percentage: number; reden: string } | null) => void;
  onSluitForm: () => void;
  onVrijgeven: () => void;
}) {
  const [pct, setPct] = useState(percentage ? String(percentage.percentage) : '70');
  const [reden, setReden] = useState(percentage?.reden ?? '');
  const [bewerken, setBewerken] = useState(false);
  if (!percentage && !formOpen) return null;
  const toonForm = formOpen || bewerken;
  const p = parseFloat(pct.replace(',', '.'));
  const geldig = Number.isFinite(p) && p > 0 && p < 100 && reden.trim().length >= 3;
  const basis = Math.round((totaal - openPosten) * 100) / 100;
  const voorbeeldNu = geldig ? Math.round(basis * p) / 100 : null;
  return (
    <div id="facturatie" className="card mb-5 border-l-4 border-accent normal-case tracking-normal scroll-mt-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-lg font-bold">Facturatie in schijven</h3>
        {percentage && !toonForm && (
          <div className="flex gap-6">
            <span className="text-sm">Nu {fmtPercentage(percentage.percentage)} <strong className="tabular-nums whitespace-nowrap">{fmtEUR(nuFactureerbaar)}</strong></span>
            <span className="text-sm">Na oplevering {fmtPercentage(100 - percentage.percentage)} <strong className="tabular-nums whitespace-nowrap text-accent-700">{fmtEUR(percentageRest)}</strong></span>
          </div>
        )}
      </div>
      {percentage && !toonForm && (
        <>
          <p className="text-sm">
            De calculatie geldt voor het volledige project. De onderaannemer mag nu {fmtPercentage(percentage.percentage)} factureren; het restpercentage pas na oplevering aan de eindklant.
          </p>
          <p className="text-xs text-secondary mt-1">Reden: {percentage.reden}{percentage.gezetDoor ? ` · ${percentage.gezetDoor}` : ''}{percentage.gezetOp ? `, ${fmtDateTime(percentage.gezetOp)}` : ''}</p>
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            {magVrijgeven && (
              <button type="button" className="btn-primary btn-sm" disabled={bezig} onClick={() => { if (confirm(`Restpercentage van ${fmtPercentage(100 - percentage.percentage)} (${fmtEUR(percentageRest)}) vrijgeven? De calculatie krijgt een nieuwe versie en er ontstaat een aanvulling voor de onderaannemer.`)) onVrijgeven(); }}>
                Restpercentage vrijgeven
              </button>
            )}
            {magWijzigen && <button type="button" className="btn-ghost btn-sm" onClick={() => setBewerken(true)}>Wijzigen</button>}
            {magWijzigen && <button type="button" className="btn-ghost btn-sm" disabled={bezig} onClick={() => { if (confirm('Het percentage weghalen? De onderaannemer mag dan alles factureren.')) onOpslaan(null); }}>Weghalen</button>}
          </div>
        </>
      )}
      {toonForm && (
        <div className="mt-1">
          <p className="text-sm mb-3">De tool rekent altijd het volledige project. Geef op welk percentage de onderaannemer nu mag factureren; de rest staat open tot na de oplevering.</p>
          <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-3 items-start">
            <label className="block">
              <span className="field-label">Nu te factureren</span>
              <span className="flex items-center gap-1">
                <input className="input text-right tabular-nums" inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} aria-label="Percentage nu te factureren" />
                <span>%</span>
              </span>
            </label>
            <label className="block">
              <span className="field-label">Reden (zichtbaar in de calculatie en de log)</span>
              <input className="input" value={reden} placeholder="bv. winterfactuur, akkoord met de onderaannemer" onChange={(e) => setReden(e.target.value)} />
            </label>
          </div>
          {voorbeeldNu != null && (
            <p className="text-sm mt-2 tabular-nums">Nu <strong>{fmtEUR(voorbeeldNu)}</strong> · na oplevering <strong>{fmtEUR(Math.round((basis - voorbeeldNu) * 100) / 100)}</strong></p>
          )}
          <div className="mt-3 flex gap-2">
            <button type="button" className="btn-primary btn-sm" disabled={!geldig || bezig} onClick={() => { onOpslaan({ percentage: p, reden: reden.trim() }); setBewerken(false); }}>
              {bezig ? 'Opslaan…' : 'Percentage opslaan'}
            </button>
            <button type="button" className="btn-ghost btn-sm" onClick={() => { setBewerken(false); onSluitForm(); }}>Annuleren</button>
          </div>
        </div>
      )}
    </div>
  );
}

const CORRECTIE_CATEGORIEEN = ['MINWERKEN', 'SCHADE', 'FRANCHISE', 'COMMERCIELE_TEGEMOETKOMING', 'BOETE'] as const;

export function CorrectieModal({ projectNummer, ploeg, bezig, onBevestig, onSluit }: {
  projectNummer: string;
  ploeg: string;
  bezig: boolean;
  onBevestig: (b: { categorie: string; omschrijving: string; reden: string; bedrag: number }) => void;
  onSluit: () => void;
}) {
  const [categorie, setCategorie] = useState<string>('MINWERKEN');
  const [omschrijving, setOmschrijving] = useState('');
  const [reden, setReden] = useState('');
  const [bedrag, setBedrag] = useState('');
  const b = parseFloat(bedrag.replace(',', '.'));
  const geldig = Number.isFinite(b) && b > 0 && reden.trim().length >= 5;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" role="dialog" aria-modal="true" aria-labelledby="correctie-titel">
      <div className="card max-w-lg w-full normal-case tracking-normal">
        <h2 id="correctie-titel" className="text-xl font-bold mb-1">Correctie na betaling</h2>
        <p className="text-sm text-secondary mb-4">
          De onderaannemer werd voor {projectNummer || 'dit project'} te veel betaald. De tool maakt een correctiecalculatie op dit project, zodat het percentage klopt,
          en zet het bedrag op {ploeg} als te verrekenen. Bij de volgende calculatie voor {ploeg} komt het er als aparte regel met dit P-nummer onder.
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="field-label">Soort</span>
            <select className="select w-full" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
              {CORRECTIE_CATEGORIEEN.map((c) => <option key={c} value={c}>{CATEGORIE_LABELS[c] ?? c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="field-label">Omschrijving</span>
            <input className="input" value={omschrijving} placeholder="bv. afvoerbuizen niet geplaatst" onChange={(e) => setOmschrijving(e.target.value)} />
          </label>
          <label className="block">
            <span className="field-label">Bedrag excl. btw dat in mindering komt</span>
            <input className="input text-right tabular-nums" inputMode="decimal" value={bedrag} placeholder="300,00" onChange={(e) => setBedrag(e.target.value)} />
          </label>
          <label className="block">
            <span className="field-label">Reden (verplicht)</span>
            <textarea className="textarea" rows={3} value={reden} placeholder="bv. de klant meldde een week na de eindfactuur dat de afvoerbuizen niet geplaatst zijn; creditnota aan de klant" onChange={(e) => setReden(e.target.value)} />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" className="btn-ghost" onClick={onSluit} disabled={bezig}>Annuleren</button>
          <button type="button" className="btn-primary" disabled={!geldig || bezig} onClick={() => onBevestig({ categorie, omschrijving: omschrijving.trim(), reden: reden.trim(), bedrag: b })}>
            {bezig ? 'Bezig…' : `Correctie van ${Number.isFinite(b) && b > 0 ? fmtEUR(b) : '…'} maken`}
          </button>
        </div>
      </div>
    </div>
  );
}

function statusTekst(v: OaVerrekening): string {
  if (v.status === 'verrekend') return `verrekend op ${v.verrekendOpProjectNummer ?? 'een volgende calculatie'}${v.verrekendOpVersie ? ` v${v.verrekendOpVersie}` : ''}${v.verrekendOp ? `, ${fmtDate(v.verrekendOp)}` : ''}`;
  if (v.verrekendOpCalcId) return `staat op ${v.verrekendOpProjectNummer ?? 'een calculatie'}, wordt verrekend bij het versturen`;
  return 'te verrekenen op de volgende calculatie';
}

/**
 * Verrekeningen bij deze calculatie (6.5): de correctie zelf, de correctie op deze broncalculatie, en wat
 * er nog openstaat op dezelfde onderaannemer (wordt bij het versturen toegevoegd).
 */
export function VerrekeningenMelding({ calcId, soort, correctieVan, verrekeningen, openOpOa, ploeg }: {
  calcId: string;
  soort: 'calculatie' | 'correctie';
  correctieVan: string | null;
  verrekeningen: OaVerrekening[];
  openOpOa: OaVerrekening[];
  ploeg: string;
}) {
  const eigen = verrekeningen.filter((v) => v.correctieCalcId === calcId);
  const opBron = verrekeningen.filter((v) => v.bronCalcId === calcId);
  const hier = openOpOa.filter((v) => !v.verrekendOpCalcId || v.verrekendOpCalcId === calcId);
  if (soort !== 'correctie' && !opBron.length && !hier.length) return null;
  return (
    <div className="mb-5 space-y-2 normal-case tracking-normal">
      {soort === 'correctie' && (
        <div className="notice" role="status">
          <strong>Correctiecalculatie na betaling.</strong> Deze calculatie corrigeert{' '}
          {correctieVan ? <Link to={`/project/${correctieVan}`} className="font-semibold underline">de oorspronkelijke calculatie</Link> : 'een eerdere calculatie'} en gaat niet naar de onderaannemer.
          {eigen.map((v) => <span key={v.id} className="block mt-1">{fmtEUR(-v.bedrag)} op {ploeg}: {statusTekst(v)}. Reden: {v.reden}</span>)}
        </div>
      )}
      {opBron.map((v) => (
        <div key={v.id} className="notice" role="status">
          <strong>Correctie na betaling:</strong> {fmtEUR(-v.bedrag)} ({CATEGORIE_LABELS[v.categorie] ?? v.categorie}{v.omschrijving ? `, ${v.omschrijving}` : ''}), {statusTekst(v)}.{' '}
          {v.correctieCalcId && <Link to={`/project/${v.correctieCalcId}`} className="font-semibold underline">Naar de correctiecalculatie</Link>}
        </div>
      ))}
      {hier.length > 0 && (
        <div className="notice border-l-4 border-warning" role="status">
          <strong>Openstaande verrekening{hier.length === 1 ? '' : 'en'} op {ploeg}.</strong> Bij het versturen {hier.length === 1 ? 'komt ze' : 'komen ze'} als aparte regel op deze calculatie, met het P-nummer van het oorspronkelijke project:
          <ul className="mt-1 list-disc pl-5">
            {hier.map((v) => <li key={v.id}>{v.bronProjectNummer || 'ander project'}{v.klantNaam ? ` (${v.klantNaam})` : ''}: <strong className="tabular-nums">{fmtEUR(-v.bedrag)}</strong>{v.omschrijving ? `, ${v.omschrijving.charAt(0).toLowerCase()}${v.omschrijving.slice(1)}` : ''}. <span className="text-secondary">{v.reden}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
